import { LANGUAGES, currentLanguage, type MessageKey, t } from '../i18n';
import { api, ApiError } from '../net/api';
import { localise } from './messages';

/**
 * The gate: the full-screen card shown whenever the player is not cleared to
 * play.
 *
 * It exists because the handshake can refuse for reasons the game screen cannot
 * usefully represent -- no session, unverified identity, a self-exclusion. A bet
 * button that cannot work is worse than a screen explaining why, so the game is
 * never opened in a half state.
 *
 * Cards are rebuilt on every showX call, so unlike the HUD they need no
 * re-render on a language change: switching language while the gate is open
 * re-presents the current card.
 */

export interface GateHandlers {
  onAuthenticated: () => void;
  /**
   * A language chosen on the sign-in screen, before there is an account to
   * store it on. The controller applies it and remembers it on the device.
   */
  onLanguagePicked: (tag: string) => Promise<void>;
}

interface CurrencyOption {
  code: string;
  name: string;
}

export class Gate {
  private readonly host: HTMLElement;
  private currencies: CurrencyOption[] = [];

  /**
   * Counter behind the generated input ids.
   *
   * A label is not a usable id source: it is translated, so the same field would
   * get a different id in every language, and in scripts like Arabic or Thai
   * there may be no ASCII left to build one from. `data-field` carries the
   * stable name instead -- the smoke script and any test look for that, never
   * for visible text.
   */
  private fieldSeq = 0;

  /** Re-presents the card that is open, for a language change. */
  private reopen: (() => void) | null = null;

  constructor(private readonly handlers: GateHandlers) {
    const el = document.getElementById('gate');
    if (!el) throw new Error('gate: #gate is missing from the page');
    this.host = el;
  }

  hide(): void {
    this.host.hidden = true;
    this.host.replaceChildren();
    this.reopen = null;
  }

  /** True while a card is on screen, so the controller knows to redraw it. */
  get isOpen(): boolean {
    return !this.host.hidden;
  }

  redraw(): void {
    this.reopen?.();
  }

  async showLogin(notice?: string): Promise<void> {
    this.reopen = () => void this.showLogin(notice);

    const card = this.card('gate.loginTitle', 'gate.loginSubtitle');
    const error = this.errorBox(card, notice);

    const identifier = this.field(card, 'gate.identifier', 'identifier', 'text', 'username');
    const password = this.field(card, 'gate.password', 'password', 'password', 'current-password');

    const submit = this.primary(card, 'gate.signIn');
    const toRegister = this.switchLink(card, 'gate.toRegister');
    this.languagePicker(card);

    const attempt = async (): Promise<void> => {
      error.hidden = true;
      submit.disabled = true;
      try {
        await api.login(identifier.value.trim(), password.value);
        this.handlers.onAuthenticated();
      } catch (e) {
        this.showError(error, e);
      } finally {
        submit.disabled = false;
      }
    };

    submit.addEventListener('click', () => void attempt());
    for (const el of [identifier, password]) {
      el.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter') void attempt();
      });
    }
    toRegister.addEventListener('click', () => void this.showRegister());

    this.present(card);
    identifier.focus();
  }

  async showRegister(): Promise<void> {
    await this.loadCurrencies();
    this.reopen = () => void this.showRegister();

    const card = this.card('gate.registerTitle', 'gate.registerSubtitle');
    const error = this.errorBox(card);

    const username = this.field(card, 'gate.username', 'username', 'text', 'username');
    const email = this.field(card, 'gate.email', 'email', 'email', 'email');
    const password = this.field(card, 'gate.newPassword', 'password', 'password', 'new-password');
    const dob = this.field(card, 'gate.dateOfBirth', 'date-of-birth', 'date', 'bday');
    const currency = this.select(
      card,
      'gate.currency',
      'currency',
      this.currencies.map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` })),
    );
    const country = this.field(card, 'gate.countryCode', 'country-code', 'text', 'country');
    country.value = 'TR';
    country.maxLength = 2;

    // The language is a field of the form, not just a display setting: it is
    // stored on the account, and the server renders anything it sends the player
    // -- a statement, a support export -- in it.
    const language = this.languageField(card);

    const terms = document.createElement('label');
    terms.className = 'check';
    const termsBox = document.createElement('input');
    termsBox.type = 'checkbox';
    termsBox.dataset['field'] = 'accept-terms';
    terms.append(termsBox, document.createTextNode(t('gate.acceptTerms')));
    card.appendChild(terms);

    const submit = this.primary(card, 'gate.register');
    const toLogin = this.switchLink(card, 'gate.toLogin');

    submit.addEventListener('click', async () => {
      error.hidden = true;
      submit.disabled = true;
      try {
        await api.register({
          username: username.value.trim(),
          email: email.value.trim(),
          password: password.value,
          currency: currency.value,
          countryCode: country.value.trim().toUpperCase(),
          dateOfBirth: dob.value,
          acceptTerms: termsBox.checked,
          language: language.value,
        });
        await api.login(username.value.trim(), password.value);
        this.handlers.onAuthenticated();
      } catch (e) {
        this.showError(error, e);
      } finally {
        submit.disabled = false;
      }
    });

    toLogin.addEventListener('click', () => void this.showLogin());

    this.present(card);
    username.focus();
  }

  /**
   * Shown when the handshake refuses. `allowDevBypass` adds the development
   * shortcuts; the server does not route them in production, so the buttons are
   * simply absent there.
   */
  /**
   * The card shown when the game cannot be opened.
   *
   * Every part of it is optional because the reasons are not alike. An account
   * that may not bet gets "retry" and "sign out". A B2B launch that failed gets
   * neither: the player is inside a casino's iframe, has no Fighter account to
   * sign out of, and retrying a spent launch token cannot succeed -- the only way
   * forward is to reopen the game from the casino, which the message says.
   */
  showBlocked(
    message: string,
    options: { allowDevBypass?: boolean; onRetry?: () => void; hideAccountActions?: boolean },
  ): void {
    this.reopen = () => this.showBlocked(message, options);

    const card = this.card('gate.blockedTitle', null, message);

    if (options.allowDevBypass && options.onRetry) {
      const note = document.createElement('p');
      note.className = 'sub';
      note.textContent = t('gate.devNote');
      card.appendChild(note);

      const verify = this.primary(card, 'gate.devVerify');
      verify.addEventListener('click', async () => {
        verify.disabled = true;
        try {
          await api.devVerifyKyc();
          options.onRetry?.();
        } catch {
          verify.disabled = false;
        }
      });
    }

    if (options.onRetry) {
      const retry = this.switchLink(card, 'gate.retry');
      retry.addEventListener('click', () => options.onRetry?.());
    }

    if (!options.hideAccountActions) {
      const signOut = this.switchLink(card, 'gate.signOut');
      signOut.addEventListener('click', async () => {
        await api.logout();
        void this.showLogin();
      });
    }

    this.present(card);
  }

  /** No balance and no payment provider yet, so this is how a dev wallet is funded. */
  showDevFaucet(onFunded: (balance: string) => void): void {
    this.reopen = () => this.showDevFaucet(onFunded);

    const card = this.card('gate.faucetTitle', 'gate.faucetSubtitle');
    const amount = this.field(card, 'gate.amount', 'amount', 'text', 'off');
    amount.value = '1000.00';

    const submit = this.primary(card, 'gate.addBalance');
    submit.addEventListener('click', async () => {
      submit.disabled = true;
      try {
        const result = await api.devDeposit(amount.value.trim());
        onFunded(result.balance);
        this.hide();
      } catch {
        submit.disabled = false;
      }
    });

    const close = this.switchLink(card, 'gate.close');
    close.addEventListener('click', () => this.hide());

    this.present(card);
  }

  /** The responsible-gambling controls a player can reach at any time. */
  showLimits(current: { dailyStakeMax?: string; weeklyStakeMax?: string }, onSaved: () => void): void {
    this.reopen = () => this.showLimits(current, onSaved);

    const card = this.card('gate.limitsTitle', 'gate.limitsSubtitle');
    const error = this.errorBox(card);

    const daily = this.field(card, 'gate.dailyLimit', 'daily-limit', 'text', 'off');
    daily.value = current.dailyStakeMax ?? '';
    const weekly = this.field(card, 'gate.weeklyLimit', 'weekly-limit', 'text', 'off');
    weekly.value = current.weeklyStakeMax ?? '';

    const save = this.primary(card, 'gate.saveLimits');
    save.addEventListener('click', async () => {
      error.hidden = true;
      save.disabled = true;
      try {
        const body: Record<string, unknown> = {};
        if (daily.value.trim() !== '') body['dailyStakeMax'] = daily.value.trim();
        if (weekly.value.trim() !== '') body['weeklyStakeMax'] = weekly.value.trim();
        const result = await api.setLimits(body);
        onSaved();
        this.hide();
        window.setTimeout(() => alert(result.message), 0);
      } catch (e) {
        this.showError(error, e);
      } finally {
        save.disabled = false;
      }
    });

    const exclude = this.switchLink(card, 'gate.selfExclude');
    exclude.style.color = 'var(--red)';
    exclude.addEventListener('click', async () => {
      const days = window.prompt(t('gate.selfExcludeDays'));
      if (days === null) return;
      const parsed = Number(days);
      if (!Number.isInteger(parsed) || parsed < 0) return;

      // The confirmation phrase itself stays English in every language: it is
      // compared byte for byte on the server, and a translated phrase would mean
      // a player could not confirm from a device set to another language.
      const confirmed = window.prompt(
        `${t('gate.selfExcludeConfirm')}\nI understand this cannot be undone`,
      );
      if (confirmed === null) return;

      try {
        await api.selfExclude(
          parsed === 0
            ? { permanent: true, confirm: confirmed }
            : { days: parsed, confirm: confirmed },
        );
        await api.logout();
        location.reload();
      } catch (e) {
        this.showError(error, e);
      }
    });

    const close = this.switchLink(card, 'gate.close');
    close.addEventListener('click', () => this.hide());

    this.present(card);
  }

  // ------------------------------------------------------------------------

  private async loadCurrencies(): Promise<void> {
    if (this.currencies.length > 0) return;
    try {
      const result = await api.currencies();
      this.currencies = result.currencies.map((c) => ({ code: c.code, name: c.name }));
    } catch {
      this.currencies = [{ code: 'TRY', name: 'Turkish lira' }];
    }
  }

  private present(card: HTMLElement): void {
    this.host.replaceChildren(card);
    this.fieldSeq = 0;
    this.host.hidden = false;
  }

  /**
   * `body` overrides the subtitle for cards whose message comes from the server
   * (a block reason) rather than from the translation table.
   */
  private card(titleKey: MessageKey, subtitleKey: MessageKey | null, body?: string): HTMLElement {
    const card = document.createElement('div');
    card.className = 'card';

    const h = document.createElement('h1');
    h.textContent = t(titleKey);
    const p = document.createElement('p');
    p.className = 'sub';
    p.textContent = body ?? (subtitleKey ? t(subtitleKey) : '');

    card.append(h, p);
    return card;
  }

  private errorBox(card: HTMLElement, notice?: string): HTMLElement {
    const box = document.createElement('div');
    box.className = 'error';
    box.hidden = !notice;
    if (notice) box.textContent = notice;
    card.appendChild(box);
    return box;
  }

  private showError(box: HTMLElement, e: unknown): void {
    box.hidden = false;
    box.textContent =
      e instanceof ApiError ? localise(e.code, e.message) : t('app.serverUnreachable');
  }

  private field(
    card: HTMLElement,
    labelKey: MessageKey,
    fieldName: string,
    type: string,
    autocomplete: string,
  ): HTMLInputElement {
    const wrap = document.createElement('div');
    wrap.className = 'field';

    const l = document.createElement('label');
    l.textContent = t(labelKey);

    const i = document.createElement('input');
    i.type = type;
    i.setAttribute('autocomplete', autocomplete);

    this.fieldSeq += 1;
    const id = `gate-field-${this.fieldSeq}`;
    i.id = id;
    l.htmlFor = id;
    i.dataset['field'] = fieldName;

    wrap.append(l, i);
    card.appendChild(wrap);
    return i;
  }

  private select(
    card: HTMLElement,
    labelKey: MessageKey,
    fieldName: string,
    options: Array<{ value: string; label: string }>,
  ): HTMLSelectElement {
    const wrap = document.createElement('div');
    wrap.className = 'field';

    const l = document.createElement('label');
    l.textContent = t(labelKey);

    const s = document.createElement('select');
    for (const o of options) {
      const opt = document.createElement('option');
      opt.value = o.value;
      opt.textContent = o.label;
      s.appendChild(opt);
    }

    s.dataset['field'] = fieldName;
    wrap.append(l, s);
    card.appendChild(wrap);
    return s;
  }

  /** The language as a stored form field, on the registration card. */
  private languageField(card: HTMLElement): HTMLSelectElement {
    const select = this.select(card, 'gate.language', 'language', languageOptions());
    select.value = currentLanguage();
    // Changing it here previews the choice, so a player sees the rest of the
    // form in the language they are about to be registered with.
    select.addEventListener('change', () => void this.handlers.onLanguagePicked(select.value));
    return select;
  }

  /**
   * A bare picker for the sign-in card.
   *
   * The topbar has one too, but the gate covers the whole screen -- and a player
   * who cannot read the sign-in form cannot reach the topbar to fix it.
   */
  private languagePicker(card: HTMLElement): HTMLSelectElement {
    const wrap = document.createElement('div');
    wrap.className = 'field';

    const l = document.createElement('label');
    l.textContent = t('gate.language');

    const s = document.createElement('select');
    for (const o of languageOptions()) {
      const opt = document.createElement('option');
      opt.value = o.value;
      opt.textContent = o.label;
      s.appendChild(opt);
    }
    s.value = currentLanguage();
    s.dataset['field'] = 'language';
    s.addEventListener('change', () => void this.handlers.onLanguagePicked(s.value));

    wrap.append(l, s);
    card.appendChild(wrap);
    return s;
  }

  private primary(card: HTMLElement, labelKey: MessageKey): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'primary';
    b.textContent = t(labelKey);
    card.appendChild(b);
    return b;
  }

  private switchLink(card: HTMLElement, labelKey: MessageKey): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'switch';
    b.textContent = t(labelKey);
    card.appendChild(b);
    return b;
  }
}

/**
 * Every language listed under its own name.
 *
 * Its own name, not a translated one: a player who has landed on the wrong
 * language cannot find "Japanese" written in Thai, but they can find 日本語.
 */
function languageOptions(): Array<{ value: string; label: string }> {
  return LANGUAGES.map((l) => ({ value: l.tag, label: l.name }));
}
